/**
 * Soft aim assist in a bout (DUEL-RING, user yes 2026-10-11). The locked foe (the persistent lock, systems/duelLock.ts,
 * Tab or a click to switch; else the other fighter of the bout) pulls a skill's aim to itself when the aim already points
 * within a cone round it, and the caster turns to face it. Outside the cone the aim is left alone, so the player can still
 * aim away. It never moves the camera. Opt-in with `setDuelV2`, like the lock.
 */
import { DUEL_FLAGS } from '../data/duelMatch';
import type { PlayerCommand } from '../world';
import { duelLockOf } from './duelLock';
import { duelMatchOf } from './duelMatch';
import type { SystemHost, WorldSystem } from './types';

type Point = { x: number; y: number };

/** Half-angle of the assist cone (degrees) and the farthest foe it reaches (metres). */
export const AIM_ASSIST = { coneDeg: 50, reach: 14 } as const;

/**
 * The aim point after assist: the foe's position when `aim` lies within `coneDeg` of the direction to the foe
 * (a missing aim means "ahead", taken as already pointing at the foe), else the aim unchanged.
 */
export function assistAim(from: Point, aim: Point | undefined, foe: Point, coneDeg: number = AIM_ASSIST.coneDeg): Point | undefined {
  const fx = foe.x - from.x;
  const fy = foe.y - from.y;
  const fl = Math.hypot(fx, fy);
  if (fl < 1e-6) return aim;
  if (!aim) return { x: foe.x, y: foe.y };
  const ax = aim.x - from.x;
  const ay = aim.y - from.y;
  const al = Math.hypot(ax, ay);
  if (al < 1e-6) return { x: foe.x, y: foe.y };
  const cos = (fx * ax + fy * ay) / (fl * al);
  return cos >= Math.cos((coneDeg * Math.PI) / 180) ? { x: foe.x, y: foe.y } : aim;
}

export class AimAssistSystem implements WorldSystem {
  readonly name = 'aimAssist';
  constructor(private readonly h: SystemHost) {}

  /** Rewrites a skill's aim toward the locked foe; never refuses the command. */
  refuse(cmd: PlayerCommand): boolean {
    if (!DUEL_FLAGS.v2 || cmd.t !== 'skill') return false;
    const w = this.h.world;
    const m = duelMatchOf(w)?.current;
    if (!m) return false;
    const p = w.player;
    const lockId = duelLockOf(w)?.lockedOf(p.id) ?? null;
    const foe = w.get(lockId ?? (m.meId === p.id ? m.foeId : m.meId));
    if (!foe || foe.dead || Math.hypot(foe.x - p.x, foe.y - p.y) > AIM_ASSIST.reach) return false;
    const next = assistAim(p, cmd.at, foe);
    if (next && next !== cmd.at) {
      cmd.at = next;
      this.h.face(p, foe);
    }
    return false;
  }
}
